import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Submission } from '@/hooks/submissions/useSubmission';
import { shouldShowSubmissionDetailPanel } from '../utils/shouldShowSubmissionDetailPanel';

interface SubmissionDetailPanelProps {
  submission: Submission;
}

const SubmissionDetailPanel: React.FC<SubmissionDetailPanelProps> = ({
  submission,
}) => {
  const isStudySiteContext =
    submission.submission_type === 'StudySite' ||
    submission.owner?.type === 'StudySite';

  const [detailTab, setDetailTab] = useState<'study' | 'site'>(
    isStudySiteContext ? 'site' : 'study'
  );

  if (!shouldShowSubmissionDetailPanel(submission)) return null;

  const studyData = submission.study;
  const studyLink = studyData?.can_view ? `/studies/${studyData.id}` : null;
  const siteData = submission.site;
  const siteLink = siteData?.can_view ? siteData.show_path : null;

  const variant = submission.submission_template?.variant;
  const variantBadge =
    variant === 'Single Site Protocol'
      ? 'Single site'
      : variant
        ? 'Multi site'
        : null;

  const submittedDate = siteData?.submitted_at
    ? new Date(siteData.submitted_at).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      })
    : '-';

  return (
    <div className="border-l border-gray-200 w-80 flex-shrink-0">
      <div className="h-full flex flex-col bg-white">
        {/* Sub-tab header */}
        <div className="flex-shrink-0 px-8 pt-6">
          {isStudySiteContext ? (
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg mb-4">
              <button
                onClick={() => setDetailTab('study')}
                className={`flex-1 px-3 py-2 rounded-md text-sm transition-all cursor-pointer ${
                  detailTab === 'study'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Study Details
              </button>
              <button
                onClick={() => setDetailTab('site')}
                className={`flex-1 px-3 py-2 rounded-md text-sm transition-all cursor-pointer ${
                  detailTab === 'site'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Site Details
              </button>
            </div>
          ) : (
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Study Details
            </h3>
          )}
        </div>

        {/* Scrollable fields */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-6 px-6 py-6">
            {detailTab === 'study' ? (
              <>
                {/* Submission ID */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">
                    Submission ID
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-gray-900 text-sm font-medium">
                      {submission.display_id || '-'}
                    </p>
                    {variantBadge && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full text-gray-600 border border-gray-300">
                        {variantBadge}
                      </span>
                    )}
                  </div>
                </div>

                {studyData?.protocol_title && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Protocol Title
                    </p>
                    <p className="text-gray-900 text-sm leading-relaxed">
                      {studyData.protocol_title}
                    </p>
                  </div>
                )}

                {studyData?.protocol_number && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Protocol Number
                    </p>
                    <p className="text-gray-900 text-sm">
                      {studyData.protocol_number}
                    </p>
                  </div>
                )}

                {studyData?.sponsor_name && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Sponsor
                    </p>
                    <p className="text-gray-900 text-sm">
                      {studyData.sponsor_name}
                    </p>
                  </div>
                )}

                {studyData?.cro_name && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      CRO
                    </p>
                    <p className="text-gray-900 text-sm">
                      {studyData.cro_name}
                    </p>
                  </div>
                )}

                {(studyData?.mapped_fields ?? []).map(field => (
                  <div key={field.attribute}>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      {field.label}
                    </p>
                    <p className="text-gray-900 text-sm">
                      {field.value || '-'}
                    </p>
                  </div>
                ))}
              </>
            ) : (
              <>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">
                    Site ID
                  </p>
                  <p className="text-gray-900 text-sm font-medium">
                    {siteData?.display_id || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">
                    Site Name
                  </p>
                  <p className="text-gray-900 text-sm">
                    {siteData?.site_name || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">
                    Submitted
                  </p>
                  <p className="text-gray-900 text-sm">{submittedDate}</p>
                </div>
                {(siteData?.mapped_fields ?? []).map(field => (
                  <div key={field.attribute}>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      {field.label}
                    </p>
                    <p className="text-gray-900 text-sm">
                      {field.value || '-'}
                    </p>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Sticky footer — one button matching the active tab */}
        {((detailTab === 'site' && siteLink) ||
          (detailTab === 'study' && studyLink)) && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-8 py-6">
            {detailTab === 'site' && siteLink && (
              <Link
                to={siteLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                View Site
              </Link>
            )}
            {detailTab === 'study' && studyLink && (
              <Link
                to={studyLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                View Study
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionDetailPanel;
